/**
 * Filling a canceled visit's time with someone who asked to come earlier.
 *
 * The switch is off until a company turns it on. A spot is offered until the
 * notice cutoff (12 hours before the visit, by default), not for a fixed
 * number of hours after the cancellation. People hear in groups. The next
 * group waits (2 hours, by default) and does not go out during quiet hours.
 *
 * Available on every plan. Texting still follows whether the company can
 * send texts at all; this feature does not add a plan of its own.
 */

import { localDateTimeToUtc, addDaysToDate } from './calculations/recurrence.ts';

export const CANCELLED_SLOT_NOTICE_HOURS_MIN = 1;
export const CANCELLED_SLOT_NOTICE_HOURS_MAX = 336;
export const CANCELLED_SLOT_NOTICE_HOURS_DEFAULT = 12;

/** How many people hear about the spot in one group. */
export const CANCELLED_SLOT_ROUND_SIZE_MIN = 1;
export const CANCELLED_SLOT_ROUND_SIZE_MAX = 20;
export const CANCELLED_SLOT_ROUND_SIZE_DEFAULT = 5;

/** How long to wait before the next group, when nobody has tapped. */
export const CANCELLED_SLOT_ROUND_HOURS_MIN = 1;
export const CANCELLED_SLOT_ROUND_HOURS_MAX = 72;
export const CANCELLED_SLOT_ROUND_HOURS_DEFAULT = 2;

/**
 * Quiet hours, as a 24-hour clock hour. A new group does not go out from
 * the start hour until the end hour. 9 PM to 8 AM is the usual window.
 * The same hour for both means "no quiet hours".
 */
export const CANCELLED_SLOT_QUIET_HOUR_MIN = 0;
export const CANCELLED_SLOT_QUIET_HOUR_MAX = 23;
export const CANCELLED_SLOT_QUIET_START_DEFAULT = 21;
export const CANCELLED_SLOT_QUIET_END_DEFAULT = 8;

/**
 * Who hears about an open spot. `same_team` (the default): only customers whose
 * next visit is done by the crew of the canceled visit. `any`: any eligible
 * customer. A canceled visit with nobody assigned goes to anyone eligible.
 */
export const CANCELLED_SLOT_AUDIENCES = ['same_team', 'any'] as const;
export type CancelledSlotAudience = (typeof CANCELLED_SLOT_AUDIENCES)[number];
export const CANCELLED_SLOT_AUDIENCE_DEFAULT: CancelledSlotAudience = 'same_team';

/** A stored audience, read safely. Anything unknown is the default. */
export function cancelledSlotAudienceFrom(raw: string | null | undefined): CancelledSlotAudience {
  return raw === 'any' ? 'any' : CANCELLED_SLOT_AUDIENCE_DEFAULT;
}

/**
 * The crew an open spot is limited to, or null when anyone eligible may hear.
 *
 * `freedTeam` is the canceled visit's team. Today that is its assigned crew
 * members. TODO(F03 saved teams): pass the visit's saved team's members once
 * saved teams ship; this rule does not change.
 */
export function offerTeamFor(audience: CancelledSlotAudience, freedTeam: readonly string[]): string[] | null {
  if (audience !== 'same_team') return null;
  const team = [...new Set(freedTeam.filter((id) => id.length > 0))];
  return team.length > 0 ? team : null;
}

/**
 * Whether a customer's visit belongs to the offer's team: at least one person
 * on that visit's crew is on the team. A visit with nobody assigned is not on
 * any team. A null team means the offer is open to anyone eligible.
 */
export function crewIsOnOfferTeam(team: readonly string[] | null, crew: readonly string[]): boolean {
  if (team === null) return true;
  return crew.some((id) => team.includes(id));
}

/** Reads `cancelled_slot_offers.team_user_ids`. Anything unreadable counts as no limit was saved. */
export function parseOfferTeam(raw: string | null | undefined): string[] | null {
  if (!raw) return null;
  try {
    const value: unknown = JSON.parse(raw);
    if (!Array.isArray(value)) return null;
    const ids = value.filter((id): id is string => typeof id === 'string' && id.length > 0);
    return ids.length > 0 ? ids : null;
  } catch {
    return null;
  }
}

/** How many matching customers one open spot will remember. Later groups come from this list. */
export const CANCELLED_SLOT_LIST_LIMIT = 100;

/** The freed spot is far enough out to be worth offering. */
export function freedSlotHasEnoughNotice(startsAt: string, nowMs: number, noticeHours: number): boolean {
  const start = Date.parse(startsAt);
  if (Number.isNaN(start)) return false;
  return start - nowMs >= noticeHours * 3_600_000;
}

/** When the offer closes: the notice cutoff before the visit. */
export function offerCutoffMs(startsAt: string, noticeHours: number): number {
  return Date.parse(startsAt) - noticeHours * 3_600_000;
}

/** The customer's job has to fit in the open spot. A longer job does not. */
export function jobFitsSlot(jobMinutes: number, slotMinutes: number): boolean {
  return jobMinutes > 0 && slotMinutes > 0 && jobMinutes <= slotMinutes;
}

/** Hour 0–23 in `timezone`, or null when the zone cannot be read. */
export function hourInZone(now: Date, timezone: string): number | null {
  try {
    const formatted = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      hour: 'numeric',
      hourCycle: 'h23',
    }).format(now);
    const hour = Number.parseInt(formatted, 10);
    if (!Number.isFinite(hour)) return null;
    return hour === 24 ? 0 : hour;
  } catch {
    return null;
  }
}

function localDate(now: Date, timezone: string): string | null {
  try {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(now);
  } catch {
    return null;
  }
}

/** True when a new group must wait. The same start and end hour means there is no quiet window. */
export function isQuietHour(hour: number, startHour: number, endHour: number): boolean {
  if (startHour === endHour) return false;
  if (startHour < endHour) return hour >= startHour && hour < endHour;
  return hour >= startHour || hour < endHour;
}

/**
 * The first moment a group may go out.
 *
 * If `at` is already outside quiet hours, it is returned as-is. Otherwise
 * the answer is the quiet-hours end (8 AM, by default) on the morning that
 * ends this quiet stretch.
 */
export function firstInstantOutsideQuiet(at: Date, timezone: string, startHour: number, endHour: number): Date {
  const hour = hourInZone(at, timezone);
  const date = localDate(at, timezone);
  if (hour === null || !date || !isQuietHour(hour, startHour, endHour)) return at;
  const endDate = startHour > endHour && hour >= startHour ? addDaysToDate(date, 1) : date;
  const hh = String(endHour).padStart(2, '0');
  return new Date(localDateTimeToUtc(endDate, `${hh}:00`, timezone));
}

/**
 * When the next group may be told.
 *
 * Null when that moment would be at or after the cutoff: there is no point
 * sending a group that cannot tap in time.
 */
export function scheduleNextRound(
  from: Date,
  roundHours: number,
  quietStartHour: number,
  quietEndHour: number,
  timezone: string,
  cutoffMs: number,
): Date | null {
  const landed = new Date(from.getTime() + roundHours * 3_600_000);
  const sendAt = firstInstantOutsideQuiet(landed, timezone, quietStartHour, quietEndHour);
  if (sendAt.getTime() >= cutoffMs) return null;
  return sendAt;
}

const WEEKDAY_EN = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;
const WEEKDAY_ES = ['dom', 'lun', 'mar', 'mie', 'jue', 'vie', 'sab'] as const;
const MONTH_EN = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const;
const MONTH_ES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'] as const;

export interface OfferClock {
  /** The visit's day, such as "Tue, Oct 6". */
  day: string;
  /** The visit's time, such as "9 AM". */
  time: string;
  /** The offer's deadline, such as "Tue 9 AM". */
  deadline: string;
}

function clockLabel(hour24: number, minute: number): string {
  const suffix = hour24 >= 12 ? 'PM' : 'AM';
  const hour12 = hour24 % 12 || 12;
  if (minute === 0) return `${hour12} ${suffix}`;
  return `${hour12}:${String(minute).padStart(2, '0')} ${suffix}`;
}

function zonedParts(
  instantIso: string,
  timezone: string,
): { weekday: number; month: number; day: number; hour: number; minute: number } | null {
  const instant = Date.parse(instantIso);
  if (Number.isNaN(instant)) return null;
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      weekday: 'short',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(new Date(instant));
    const read = (type: string) => parts.find((part) => part.type === type)?.value ?? '';
    const weekdayName = read('weekday');
    const weekday = WEEKDAY_EN.indexOf(weekdayName as (typeof WEEKDAY_EN)[number]);
    const month = Number(read('month'));
    const day = Number(read('day'));
    let hour = Number(read('hour'));
    const minute = Number(read('minute'));
    if (hour === 24) hour = 0;
    if (weekday < 0 || !month || !day || !Number.isFinite(hour) || !Number.isFinite(minute)) return null;
    return { weekday, month, day, hour, minute };
  } catch {
    return null;
  }
}

/** Day, time, and deadline in the company's time zone, in the short form the messages use. */
export function formatOfferClock(instantIso: string, timezone: string, language: 'en' | 'es'): OfferClock | null {
  const parts = zonedParts(instantIso, timezone);
  if (!parts) return null;
  const weekdays = language === 'es' ? WEEKDAY_ES : WEEKDAY_EN;
  const months = language === 'es' ? MONTH_ES : MONTH_EN;
  const weekday = weekdays[parts.weekday] ?? WEEKDAY_EN[parts.weekday];
  const month = months[parts.month - 1] ?? MONTH_EN[parts.month - 1];
  const time = clockLabel(parts.hour, parts.minute);
  return {
    day: `${weekday}, ${month} ${parts.day}`,
    time,
    deadline: `${weekday} ${time}`,
  };
}
